import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["commentsFrame", "toggleButton", "commentCount", "commentForm"]
  static values = { 
    postId: Number,
    expanded: Boolean
  }

  connect() {
    console.log('💬 Rails 8 Inline Comments Controller connected for post', this.postIdValue)
    this.expandedValue = false
    
    // Check if URL has a comment anchor and expand comments if needed
    this.handleCommentAnchor()
  }

  toggle() {
    console.log('🔄 Toggling comments for post', this.postIdValue, 'currently expanded:', this.expandedValue)
    
    if (this.expandedValue) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  expand() {
    console.log('📖 Expanding comments for post', this.postIdValue)
    
    // Close all other open comment sections before expanding this one
    this.closeOtherCommentSections()
    
    this.expandedValue = true
    
    // Update button styling to show active state
    if (this.hasToggleButtonTarget) {
      const commentText = this.hasCommentCountTarget ? 
        this.commentCountTarget.textContent : '0'
      
      this.toggleButtonTarget.innerHTML = `
        <i class="fas fa-comment mr-1.5"></i>
        ${commentText}
      `
      this.toggleButtonTarget.classList.add('text-blue-600', 'dark:text-night-primary')
    }
    
    // Show the comments frame - it will lazy load via Turbo Frame
    if (this.hasCommentsFrameTarget) {
      this.commentsFrameTarget.classList.remove('hidden')
      
      // Add smooth expand animation
      this.commentsFrameTarget.style.maxHeight = '0'
      this.commentsFrameTarget.style.overflow = 'hidden'
      this.commentsFrameTarget.style.transition = 'max-height 0.3s ease-out'
      
      // Trigger the animation
      requestAnimationFrame(() => {
        this.commentsFrameTarget.style.maxHeight = '2000px'
        setTimeout(() => {
          this.commentsFrameTarget.style.maxHeight = 'none'
          this.commentsFrameTarget.style.overflow = 'visible'
          
          // Don't auto-focus to avoid focus jumping issues
          console.log('💬 Comments loaded and expanded')
        }, 300)
      })
    }
  }

  collapse() {
    console.log('📕 Collapsing comments for post', this.postIdValue)
    this.expandedValue = false
    
    // Update button styling back to inactive state
    if (this.hasToggleButtonTarget) {
      const commentText = this.hasCommentCountTarget ? 
        this.commentCountTarget.textContent : '0'
      
      this.toggleButtonTarget.innerHTML = `
        <i class="fas fa-comment mr-1.5 text-gray-400"></i>
        ${commentText}
      `
      this.toggleButtonTarget.classList.remove('text-blue-600', 'dark:text-night-primary')
    }
    
    // Hide the comments frame with animation
    if (this.hasCommentsFrameTarget) {
      this.commentsFrameTarget.style.maxHeight = this.commentsFrameTarget.scrollHeight + 'px'
      this.commentsFrameTarget.style.overflow = 'hidden'
      this.commentsFrameTarget.style.transition = 'max-height 0.3s ease-out'
      
      requestAnimationFrame(() => {
        this.commentsFrameTarget.style.maxHeight = '0'
        setTimeout(() => {
          this.commentsFrameTarget.classList.add('hidden')
          this.commentsFrameTarget.style.transition = ''
          this.commentsFrameTarget.style.overflow = ''
          this.commentsFrameTarget.style.maxHeight = ''
        }, 300)
      })
    }
  }

  // Handle comment count updates from Turbo Streams
  updateCommentCount(event) {
    const newCount = event.detail.count
    console.log('📊 Updating comment count for post', this.postIdValue, 'to', newCount)
    
    if (this.hasCommentCountTarget) {
      this.commentCountTarget.textContent = newCount
    }
    
    // Update button text based on expanded state
    if (this.hasToggleButtonTarget) {
      const iconColor = this.expandedValue ? '' : ' text-gray-400'
      this.toggleButtonTarget.innerHTML = `
        <i class="fas fa-comment mr-1.5${iconColor}"></i>
        ${newCount}
      `
    }
  }

  // Handle successful comment submission
  commentAdded(event) {
    console.log('✅ Comment added successfully for post', this.postIdValue)
    
    // Clear the comment form without changing focus
    if (this.hasCommentFormTarget) {
      const textarea = this.commentFormTarget.querySelector('textarea')
      if (textarea) {
        textarea.value = ''
        // Reset auto-resize
        textarea.style.height = 'auto'
        // Don't refocus to avoid focus jumping
      }
    }
    
    // Show success feedback
    this.showFeedback('Comment added successfully!', 'success')
  }

  // Handle failed comment submission  
  commentFailed(event) {
    console.log('❌ Comment failed for post', this.postIdValue, event.detail)
    this.showFeedback('Failed to add comment. Please try again.', 'error')
  }

  showFeedback(message, type) {
    // Create temporary feedback element
    const feedback = document.createElement('div')
    feedback.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
      type === 'success' 
        ? 'bg-green-100 text-green-800 border border-green-200' 
        : 'bg-red-100 text-red-800 border border-red-200'
    }`
    feedback.textContent = message
    feedback.style.opacity = '0'
    feedback.style.transform = 'translateY(-10px)'
    
    document.body.appendChild(feedback)
    
    // Animate in
    requestAnimationFrame(() => {
      feedback.style.opacity = '1'
      feedback.style.transform = 'translateY(0)'
    })
    
    // Remove after delay
    setTimeout(() => {
      feedback.style.opacity = '0'
      feedback.style.transform = 'translateY(-10px)'
      setTimeout(() => {
        document.body.removeChild(feedback)
      }, 300)
    }, 3000)
  }
  
  // Handle comment anchor in URL (e.g., #comment-123)
  handleCommentAnchor() {
    const hash = window.location.hash
    if (hash && hash.startsWith('#comment-')) {
      console.log('🎯 Found comment anchor:', hash, 'expanding comments for post', this.postIdValue)
      
      // Expand comments to show the target comment
      this.expand()
      
      // Wait for comments to load and then scroll to the comment
      this.scrollToCommentWhenLoaded(hash)
    }
  }
  
  // Wait for comments to load and scroll to specific comment
  scrollToCommentWhenLoaded(commentAnchor) {
    // Create a function to check for the comment and scroll
    const scrollToComment = () => {
      let commentElement = document.querySelector(commentAnchor)
      
      if (commentElement) {
        console.log('📍 Found target comment, checking if it\'s a nested reply:', commentAnchor)
        
        // Check if this comment is inside a hidden replies container
        const repliesContainer = commentElement.closest('[data-comment-replies-target="repliesContainer"]')
        
        if (repliesContainer && repliesContainer.classList.contains('hidden')) {
          console.log('🔓 Comment is in hidden replies container, expanding parent comment')
          
          // Find the parent comment's reply button and click it
          const parentComment = repliesContainer.closest('[data-controller*="comment-replies"]')
          const toggleButton = parentComment?.querySelector('[data-action*="toggleReplies"]')
          
          if (toggleButton) {
            // Simulate clicking the toggle button to show replies
            toggleButton.click()
            
            // Wait for the animation to complete, then scroll
            setTimeout(() => {
              commentElement = document.querySelector(commentAnchor) // Re-query after expansion
              if (commentElement) {
                this.scrollToAndHighlightComment(commentElement)
              }
            }, 400) // Wait for expand animation
            
            return true
          }
        } else {
          // Comment is already visible, scroll to it
          this.scrollToAndHighlightComment(commentElement)
          return true
        }
      }
      return false // Not found yet
    }
    
    // Try scrolling immediately (in case comments are already loaded)
    if (scrollToComment()) {
      return
    }
    
    // If not found, wait for the turbo frame to load
    const frame = this.commentsFrameTarget?.querySelector('turbo-frame')
    if (frame) {
      // Listen for frame load
      frame.addEventListener('turbo:frame-load', () => {
        console.log('🔄 Comments frame loaded, attempting to scroll to comment')
        setTimeout(scrollToComment, 100) // Small delay to ensure DOM is ready
      }, { once: true })
      
      // Fallback: keep checking for the comment periodically
      let attempts = 0
      const maxAttempts = 20 // Wait up to 10 seconds
      
      const checkInterval = setInterval(() => {
        attempts++
        
        if (scrollToComment() || attempts >= maxAttempts) {
          clearInterval(checkInterval)
          if (attempts >= maxAttempts) {
            console.log('⚠️ Could not find comment after', maxAttempts, 'attempts')
          }
        }
      }, 500)
    }
  }
  
  // Scroll to comment and add attention-grabbing highlight
  scrollToAndHighlightComment(commentElement) {
    console.log('🎯 Scrolling to and highlighting comment')
    
    // Scroll to the comment with smooth behavior, positioned in center for better visibility
    commentElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    })
    
    // Add an attention-grabbing flash animation
    this.flashComment(commentElement)
  }
  
  // Attention-grabbing flash animation for comment
  flashComment(commentElement) {
    console.log('✨ Adding attention-grabbing flash to comment')
    
    // Store original styles
    const originalStyles = {
      backgroundColor: commentElement.style.backgroundColor,
      borderLeft: commentElement.style.borderLeft,
      boxShadow: commentElement.style.boxShadow,
      transform: commentElement.style.transform,
      transition: commentElement.style.transition
    }
    
    // Check if we're in dark mode
    const isDarkMode = document.documentElement.classList.contains('dark')
    
    // Initial flash - bright attention-grabbing color with dark mode support
    const flashColor = isDarkMode ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.3)' // Blue flash
    const borderColor = isDarkMode ? '#3b82f6' : '#2563eb' // Blue border
    const glowColor = isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)' // Blue glow
    
    commentElement.style.backgroundColor = flashColor
    commentElement.style.borderLeft = `6px solid ${borderColor}`
    commentElement.style.boxShadow = `0 0 20px ${glowColor}`
    commentElement.style.transform = 'scale(1.02)' // Slight scale up
    commentElement.style.transition = 'all 0.3s ease-out'
    
    // Pulse effect with multiple flashes
    let flashCount = 0
    const maxFlashes = 3
    
    const pulseInterval = setInterval(() => {
      flashCount++
      
      const isDarkMode = document.documentElement.classList.contains('dark')
      
      if (flashCount % 2 === 0) {
        // Flash on - use blue colors for better readability
        const flashOnColor = isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.25)'
        const flashOnShadow = isDarkMode ? '0 0 15px rgba(59, 130, 246, 0.3)' : '0 0 15px rgba(59, 130, 246, 0.4)'
        commentElement.style.backgroundColor = flashOnColor
        commentElement.style.boxShadow = flashOnShadow
      } else {
        // Flash off (but still highlighted)
        const flashOffColor = isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'
        const flashOffShadow = isDarkMode ? '0 0 10px rgba(59, 130, 246, 0.2)' : '0 0 10px rgba(59, 130, 246, 0.15)'
        commentElement.style.backgroundColor = flashOffColor
        commentElement.style.boxShadow = flashOffShadow
      }
      
      if (flashCount >= maxFlashes * 2) {
        clearInterval(pulseInterval)
        
        // Final state - subtle blue highlight with dark mode support
        const isDarkMode = document.documentElement.classList.contains('dark')
        const finalBgColor = isDarkMode ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)'
        const finalShadow = isDarkMode ? '0 2px 8px rgba(59, 130, 246, 0.15)' : '0 2px 8px rgba(59, 130, 246, 0.1)'
        
        commentElement.style.backgroundColor = finalBgColor
        commentElement.style.borderLeft = '4px solid #3b82f6'
        commentElement.style.boxShadow = finalShadow
        commentElement.style.transform = 'scale(1)'
        
        // Remove all effects after a few more seconds
        setTimeout(() => {
          commentElement.style.backgroundColor = originalStyles.backgroundColor
          commentElement.style.borderLeft = originalStyles.borderLeft
          commentElement.style.boxShadow = originalStyles.boxShadow
          commentElement.style.transform = originalStyles.transform
          
          setTimeout(() => {
            commentElement.style.transition = originalStyles.transition
          }, 300)
        }, 4000)
      }
    }, 400) // Flash every 400ms
    
    // Add a subtle bounce animation
    commentElement.style.animation = 'gentle-bounce 0.6s ease-out'
    
    // Clean up animation
    setTimeout(() => {
      commentElement.style.animation = ''
    }, 600)
  }
  
  // Legacy highlight method (kept for compatibility)
  highlightComment(commentElement) {
    this.flashComment(commentElement)
  }

  // Close all other open comment sections on the page
  closeOtherCommentSections() {
    // Lock the position of the current post to prevent scroll jumping
    this.lockCurrentPostPosition()
    
    // Find all inline comments controllers on the page
    const allInlineCommentsControllers = document.querySelectorAll('[data-controller*="inline-comments"]')
    
    let closingPromises = []
    
    allInlineCommentsControllers.forEach(element => {
      // Skip the current controller
      if (element === this.element) return
      
      // Get the controller instance
      const controller = this.application.getControllerForElementAndIdentifier(element, 'inline-comments')
      if (controller && controller.expandedValue) {
        console.log('🔒 Auto-closing comments for post', controller.postIdValue)
        
        // Create a promise that resolves when the collapse animation completes
        const collapsePromise = new Promise(resolve => {
          // Set a timeout that matches the collapse animation duration
          setTimeout(resolve, 300)
        })
        closingPromises.push(collapsePromise)
        
        controller.collapse()
      }
    })
    
    // If we have closing sections, wait for them to complete
    if (closingPromises.length > 0) {
      Promise.all(closingPromises).then(() => {
        // Use requestAnimationFrame to ensure DOM changes are painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.unlockCurrentPostPosition()
          })
        })
      })
    } else {
      // No sections to close, unlock immediately
      console.log('ℹ️ No sections to close, unlocking position immediately')
      this.unlockCurrentPostPosition()
    }
  }
  
  lockCurrentPostPosition() {
    // The inline-comments controller is attached to the post element itself
    let currentPost = this.element
    
    // If that doesn't work, try finding the post container
    if (!currentPost.id || !currentPost.id.startsWith('post-')) {
      currentPost = this.element.closest('article') || 
                   this.element.closest('[id^="post-"]') || 
                   this.element.closest('.post') || 
                   this.element.closest('[data-post-id]')
    }
    
    if (!currentPost) {
      console.log('❌ Could not find post element for position locking. Element:', this.element)
      return
    }
    
    // Store current position
    const rect = currentPost.getBoundingClientRect()
    this.lockedPost = currentPost
    this.lockedScrollPosition = window.scrollY
    this.lockedPostTop = rect.top
    
    console.log('🔒 Locking post position at', rect.top, 'scroll:', window.scrollY, 'element:', currentPost)
    
    // Also store the page height to detect changes
    this.lockedPageHeight = document.documentElement.scrollHeight
    console.log('📏 Page height before changes:', this.lockedPageHeight)
  }
  
  unlockCurrentPostPosition() {
    if (!this.lockedPost) {
      console.log('⚠️ No locked post to unlock')
      return
    }
    
    // Check how much the page height changed
    const currentPageHeight = document.documentElement.scrollHeight
    const pageHeightDiff = currentPageHeight - this.lockedPageHeight
    console.log('📏 Page height changed by:', pageHeightDiff)
    
    // Calculate how much the post has moved
    const currentRect = this.lockedPost.getBoundingClientRect()
    const positionDiff = currentRect.top - this.lockedPostTop
    
    console.log('🔍 Post moved by:', positionDiff, 'pixels')
    console.log('📊 Current post top:', currentRect.top, 'Original top:', this.lockedPostTop)
    console.log('📜 Current scroll:', window.scrollY, 'Original scroll:', this.lockedScrollPosition)
    
    if (Math.abs(positionDiff) > 1) { // Only adjust if there's significant movement
      const newScrollPosition = this.lockedScrollPosition - positionDiff
      console.log('📍 Adjusting scroll from', window.scrollY, 'to', newScrollPosition)
      
      window.scrollTo({
        top: newScrollPosition,
        behavior: 'instant' // No smooth scrolling for position corrections
      })
      
      // Verify the correction worked
      setTimeout(() => {
        const verifyRect = this.lockedPost.getBoundingClientRect()
        console.log('✅ After correction, post is at:', verifyRect.top, 'target was:', this.lockedPostTop)
      }, 10)
    } else {
      console.log('✅ Post position is stable, no correction needed')
    }
    
    // Clean up
    this.lockedPost = null
    this.lockedScrollPosition = null
    this.lockedPostTop = null
    this.lockedPageHeight = null
  }
}